import React, { useMemo } from 'react';
import TrashIcon from "../../../../../../assets/svg/icons/common/Trash";
import FileIcon from "../../../../../../assets/svg/icons/common/FileIcon";
import { Select } from "../../../../../../components/form/select";
import { getFileTypeInfo, FileIconColorMapping } from './utils';
import { DOCUMENT_TYPE_OPTIONS } from './constants';
import styles from './style.module.scss';

/**
 * File preview component showing file details and type selector
 * 
 * @component
 * @param {Object} props - Component props
 * @param {Object} props.file - File object containing name and details
 * @param {string} props.selectedType - Currently selected file type
 * @param {Function} props.onTypeChange - Handler for type change
 * @param {Function} props.onRemove - Handler for file removal
 * @returns {React.ReactElement} FilePreviewRow component
 */
const FilePreviewRow = ({ file, selectedType, onTypeChange, onRemove }) => {
  // Extract file information using memoization for performance
  const { extension, nameWithoutExtension, color } = useMemo(() => {
    const ext = file.name.split('.').pop().toLowerCase();
    const nameWithoutExt = file.name.replace(`.${ext}`, '');
    const { color } = getFileTypeInfo(file.name, selectedType);
    
    return {
      extension: ext,
      nameWithoutExtension: nameWithoutExt,
      color
    };
  }, [file.name, selectedType]);

  /**
   * Handle file type change
   * @param {string} value - New file type value
   */
  const handleTypeChange = (value) => {
    onTypeChange(file.name, value);
  };

  /**
   * Handle file removal
   */
  const handleRemove = () => {
    onRemove(file.name);
  };

  return (
    <div className={styles.fileRow}>
      <div className={styles.filePreview}>
        <div className={styles.fileIconWrapper}>
          <FileIcon color={FileIconColorMapping[extension]} />
        </div>
        <div className={styles.fileNameWrapper}>
          <span className={styles.fileNameText}>{nameWithoutExtension}</span>
          <span className={styles.fileExtension}>.{extension}</span>
        </div>
        <button 
          className={styles.trashButton}
          onClick={handleRemove}
          type="button"
          aria-label="Remove file"
        >
          <TrashIcon color="#A4A3A8" />
        </button>
      </div>
      <div className={styles.fileTypeSelectWrapper}>
        <Select
          placeholder="Select document type"
          value={selectedType}
          options={DOCUMENT_TYPE_OPTIONS}
          onChange={handleTypeChange}
          className={styles.typeSelect}
        />
      </div>
    </div>
  );
};

export default FilePreviewRow; 